/**
 * ConfirmacaoEmailModal
 * ---------------------
 * Segundo passo do fluxo "Registrar Inscrição": após a vendedora preencher
 * quantidade/valor/alunos, este modal mostra:
 *  - assunto do e-mail (editável)
 *  - corpo HTML pré-gerado (editável — textarea de HTML)
 *  - destinatário (pré-preenchido do município, editável)
 *  - preview inline
 *  - botão "Editar" e "Confirmar e enviar"
 *
 * Ao confirmar → chama onConfirm({ enviar_email, email_destinatario, email_assunto, email_corpo_html })
 * Ao pular → chama onConfirm({ enviar_email: false })
 */
import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Mail, Pencil, Eye, Send, AlertCircle, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

// Iter 40: chips de e-mails múltiplos
function EmailChipsRow({ emails, onChange }) {
  const [input, setInput] = useState("");
  const isValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const add = () => {
    const v = input.trim().replace(/,$/, "");
    if (!v) return;
    if (!isValid(v)) { toast.error(`E-mail inválido: ${v}`); return; }
    if ((emails || []).includes(v)) { setInput(""); return; }
    onChange([...(emails || []), v]);
    setInput("");
  };
  return (
    <div
      className="min-h-[42px] flex flex-wrap items-center gap-1.5 p-2 border border-slate-300 rounded-md bg-white"
      data-testid="conf-destinatarios-chips"
    >
      {(emails || []).map((e) => (
        <span
          key={e}
          className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-full pl-2 pr-1 py-0.5 text-xs"
          data-testid={`conf-email-chip-${e}`}
        >
          <Mail className="w-3 h-3" />
          {e}
          <button
            type="button"
            onClick={() => onChange((emails || []).filter((x) => x !== e))}
            className="ml-1 w-4 h-4 rounded-full bg-white hover:bg-red-100 hover:text-red-600 text-slate-500 flex items-center justify-center text-[10px]"
            aria-label={`Remover ${e}`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
      <input
        type="email"
        value={input}
        onChange={(ev) => setInput(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === ",") { ev.preventDefault(); add(); }
          else if (ev.key === "Backspace" && !input && (emails || []).length > 0) {
            onChange((emails || []).slice(0, -1));
          }
        }}
        onBlur={add}
        placeholder={(emails || []).length === 0 ? "email@camaramunicipal.gov.br" : ""}
        className="flex-1 min-w-[180px] border-0 outline-0 text-sm bg-transparent"
        data-testid="conf-destinatario-input"
      />
    </div>
  );
}

export default function ConfirmacaoEmailModal({ open, onClose, onConfirm, previewPayload }) {
  const [loading, setLoading] = useState(false);
  const [destinatarios, setDestinatarios] = useState([]); // Iter 40: lista de e-mails (chips)
  const [assunto, setAssunto] = useState("");
  const [corpoHtml, setCorpoHtml] = useState("");
  const [mode, setMode] = useState("preview"); // preview | edit
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!open || !previewPayload) return;
    setLoading(true); setErro("");
    api.post("/inscricoes/preview-confirmacao", previewPayload)
      .then((r) => {
        setAssunto(r.data.assunto || "");
        setCorpoHtml(r.data.corpo_html || "");
        // Iter 40: preview retorna { destinatario_sugerido, destinatarios_sugeridos? }
        const lista = r.data.destinatarios_sugeridos && Array.isArray(r.data.destinatarios_sugeridos)
          ? r.data.destinatarios_sugeridos.filter((e) => e && e.includes("@"))
          : (r.data.destinatario_sugerido && r.data.destinatario_sugerido.includes("@")
              ? [r.data.destinatario_sugerido] : []);
        setDestinatarios(lista);
      })
      .catch((e) => {
        setErro(e?.response?.data?.detail || "Não foi possível gerar o preview do e-mail.");
      })
      .finally(() => setLoading(false));
  }, [open, previewPayload]);

  const enviarEConfirmar = () => {
    if (!destinatarios || destinatarios.length === 0) {
      toast.error("Informe pelo menos um e-mail de destino.");
      return;
    }
    if (!assunto.trim()) {
      toast.error("O assunto do e-mail é obrigatório.");
      return;
    }
    onConfirm({
      enviar_email: true,
      // Retrocompat: mantemos email_destinatario (primeiro) + envia lista completa em email_destinatarios
      email_destinatario: destinatarios[0],
      email_destinatarios: destinatarios,
      email_assunto: assunto.trim(),
      email_corpo_html: corpoHtml,
    });
  };

  const pularEnvio = () => onConfirm({ enviar_email: false });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="confirmacao-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#1d7495]" />
            Confirmação de Inscrição — E-mail oficial
          </DialogTitle>
          <p className="text-xs text-slate-500">
            Um PDF oficial com os dados da PJ emissora será gerado e anexado. Revise o assunto/corpo e confirme o envio.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Gerando preview e PDF…
          </div>
        ) : erro ? (
          <div className="py-8 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">{erro}</div>
          </div>
        ) : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label>Destinatários</Label>
              <EmailChipsRow emails={destinatarios} onChange={setDestinatarios} />
              <div className="text-[11px] text-slate-500 mt-1">
                Digite um e-mail e pressione Enter ou vírgula. Todos os endereços receberão a confirmação.
              </div>
            </div>

            <div>
              <Label>Assunto</Label>
              <Input
                value={assunto}
                onChange={(e) => setAssunto(e.target.value)}
                data-testid="conf-assunto"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Corpo do e-mail</Label>
                <div className="flex items-center gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setMode("preview")}
                    className={`px-2 py-1 rounded ${mode === "preview" ? "bg-[#1d7495] text-white" : "bg-slate-100 text-slate-600"}`}
                    data-testid="conf-mode-preview"
                  >
                    <Eye className="w-3 h-3 inline mr-1" /> Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("edit")}
                    className={`px-2 py-1 rounded ${mode === "edit" ? "bg-[#1d7495] text-white" : "bg-slate-100 text-slate-600"}`}
                    data-testid="conf-mode-edit"
                  >
                    <Pencil className="w-3 h-3 inline mr-1" /> Editar HTML
                  </button>
                </div>
              </div>
              {mode === "preview" ? (
                <div
                  className="rounded-lg border border-slate-200 bg-white p-4 min-h-[280px] max-h-[420px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: corpoHtml }}
                  data-testid="conf-preview"
                />
              ) : (
                <textarea
                  value={corpoHtml}
                  onChange={(e) => setCorpoHtml(e.target.value)}
                  rows={16}
                  className="w-full rounded-lg border border-slate-200 p-3 text-xs font-mono bg-slate-50"
                  data-testid="conf-corpo"
                />
              )}
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
              <b>📎 Anexo:</b> Um PDF oficial <i>Confirmação de Inscrição</i> será gerado automaticamente com os dados da PJ emissora do curso (razão social, CNPJ, endereço, dados bancários e PIX).
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={pularEnvio} data-testid="conf-pular">
            Salvar sem enviar e-mail
          </Button>
          <Button
            onClick={enviarEConfirmar}
            disabled={loading || !!erro}
            className="bg-[#1d7495] hover:bg-[#155a75]"
            data-testid="conf-confirmar"
          >
            <Send className="w-4 h-4 mr-1" /> Confirmar e enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
